import React, { useState, useRef, useContext } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { MyContext } from "../../MyContextApi";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import TablePagination from "@mui/material/TablePagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faMinimize } from "@fortawesome/free-solid-svg-icons";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";

import "./../../styles/TextAnalysisTable.css";
// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#1AACAC",
    color: theme.palette.common.white,
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    padding: theme.spacing(1.2),
    lineHeight: "1.2rem",
    border: "none", // Reset all borders
    borderRight: `1px solid ${theme.palette.divider}`, // Only add right border
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    backgroundColor: theme.palette.common.white,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    padding: theme.spacing(1.2),
    border: `0.5px solid ${theme.palette.divider}`,
    // border: "none",
    // Reset all borders
    // borderRight: `1px solid ${theme.palette.divider}`,
    // Only add right border
    "&:last-child": {
      borderRight: "none", // Remove right border from last column
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: "rgba(249, 84, 84, 0.04)",
    transition: "background-color 0.2s ease",
  },
}));

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange, paginatedData } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const disableButtons = paginatedData.length === 0;

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={disableButtons || page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={disableButtons || page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={disableButtons || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={disableButtons || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

/*************  ✨ Windsurf Command 🌟  *************/
function SentimentAnalysisTable({ analysisData = [], tableRef }) {
  const { navigationCount, setNavigationCount } = useContext(MyContext);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tableContainerRef = useRef();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      tableContainerRef.current.requestFullscreen();
      setIsFullscreen(isFullscreen);
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > page) {
      setNavigationCount((prev) => prev + 1);
    } else {
      setNavigationCount((prev) => prev - 1);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = Array.isArray(analysisData)
    ? analysisData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  //
  const formatDateTime = (datetime) => {
    try {
      // Handle null, undefined, or empty values
      if (!datetime) {
        return "No Date";
      }

      const dateTime = new Date(datetime);

      // Check if the date is valid
      if (isNaN(dateTime.getTime())) {
        return "Invalid Date";
      }

      const dateFormatted = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        timeZone: "UTC",
      }).format(dateTime);

      const timeFormatted = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "UTC",
      }).format(dateTime);

      return `${dateFormatted}, ${timeFormatted}`;
    } catch (error) {
      console.error("DateTime formatting error:", error, "Input:", datetime);
      return "Invalid Date";
    }
  };
  return (
    <Box
      sx={{
        width: isFullscreen ? "100vw" : "auto",
        height: isFullscreen ? "100vh" : "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
        overflow: "hidden",
        position: "relative",
        zIndex: 0,
        borderRadius: "15px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.25)", // Added box shadow
        // marginTop: "30px",
      }}
    >
      <TableContainer
        component={Paper}
        className="custom-table-container"
        ref={tableContainerRef}
      >
        <Table
          stickyHeader
          sx={{ minWidth: 1200, tableLayout: "fixed" }}
          aria-label="customized table"
          ref={tableRef}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: 75 }}>S.No</StyledTableCell>
              <StyledTableCell sx={{ width: 100 }}> Keyword </StyledTableCell>
              <StyledTableCell sx={{ width: 100 }}>DateTime</StyledTableCell>

              <StyledTableCell sx={{ width: 115 }}>Platform</StyledTableCell>

              <StyledTableCell sx={{ width: 150 }}>User Name</StyledTableCell>

              <StyledTableCell sx={{ width: 400 }}>Content</StyledTableCell>

              <StyledTableCell sx={{ width: 100 }}> Sentiment </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 &&
              paginatedData.map((data, index) => {
                const formattedDateTime = data.datetime
                  ? data.datetime
                      .replace("T", " ") // Replacing the 'T' separator between date and time
                      .split(".")[0] // Removing the fractional seconds and the timezone part
                      .replace(" ", " / ") // Replacing space between date and time with a '/'
                  : "N/A";

                return (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      {page * rowsPerPage + index + 1}
                    </StyledTableCell>

                    <StyledTableCell>
                      {data.keywords
                        ? data.keywords
                        : data.keyword
                        ? data.keyword
                        : "N/A"}
                    </StyledTableCell>

                    <StyledTableCell>
                      {formatDateTime(data.datetime)}
                    </StyledTableCell>

                    <StyledTableCell>
                      {data.platform ? data.platform : "N/A"}
                    </StyledTableCell>

                    <StyledTableCell>
                      {data.postOwner ? data.postOwner : "N/A"}
                    </StyledTableCell>

                    <StyledTableCell>
                      {data.platform === "youtube" ? (
                        data.content && data.content[0]?.title ? (
                          data.link ? (
                            <a
                              href={data.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "black",
                                textDecoration: "none",
                                cursor: "pointer",
                              }}
                            >
                              {data.content[0].title}
                            </a>
                          ) : (
                            data.content[0].title
                          )
                        ) : (
                          "N/A"
                        )
                      ) : data.content ? (
                        data.link ? (
                          <a
                            href={data.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "black",
                              textDecoration: "none",
                              cursor: "pointer",
                            }}
                          >
                            {data.content}
                          </a>
                        ) : (
                          data.content
                        )
                      ) : (
                        "N/A"
                      )}
                    </StyledTableCell>

                    <StyledTableCell>
                      {data.analysis
                        ? data.analysis.prediction
                        : data.sentiment
                        ? data.sentiment
                        : "N/A"}
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          outline: "none",
          backgroundColor: "white",
          borderBottomRightRadius: "15px",
          borderBottomLeftRadius: "15px",
        }}
      >
         
        <Tooltip title="Expand">
          <Button onClick={toggleFullscreen}>
            {isFullscreen ? (
              <FontAwesomeIcon icon={faMinimize} fontSize={"15px"} />
            ) : (
              <FontAwesomeIcon icon={faExpand} fontSize={"20px"} />
            )}
          </Button>
        </Tooltip>
        <TablePagination
          component="div"
          count={Array.isArray(analysisData) ? analysisData.length : 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={(props) => (
            <TablePaginationActions {...props} paginatedData={paginatedData} />
          )}
          sx={{
            minWidth: "auto",
            // backgroundColor: "blue",
            height: "50px", // Adjust the height
            "& .MuiTablePagination-toolbar": {
              minHeight: "50px", // Ensures the toolbar matches the container height
              padding: "0 8px", // Reduce padding for a compact design
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "15px", // Smaller font for text
                margin: 0, // Remove unnecessary margin
              },
            "& .MuiTablePagination-actions": {
              padding: 0, // Remove padding around actions
            },
            "& .MuiSelect-select": {
              fontSize: "15px", // Adjust font size of dropdown
              padding: "4px 8px", // Compact dropdown size
            },
          }}
        />
      </Box>
    </Box>
  );
}
/*******  7b44fd9c-4cb1-46a8-8ff7-2530900886d5  *******/

export default SentimentAnalysisTable;
